/**
 * Public Media & Press library at `/media`.
 *
 * Lists all published items grouped into category tabs:
 *   All · Promo Videos · Flyers · Testimonials · How-To · Press
 *
 * Each card deep-links to `/media/<slug>` so the operator can paste
 * the exact URL into emails / text messages / radio-spot landing
 * pages instead of leaking traffic to Instagram or Facebook.
 *
 * Mirrors `About.jsx`'s self-contained shell (no AppShell) so logged-
 * out radio listeners and email recipients can browse without an
 * auth wall.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import { VideoCameraIcon as VideoCamera, FilePdfIcon as FilePdf, ImageIcon as ImageIcon, MegaphoneIcon as Megaphone, ChatCircleTextIcon as ChatCircleText, NewspaperIcon as Newspaper, GraduationCapIcon as GraduationCap, PlayCircleIcon as PlayCircle, SparkleIcon as Sparkle } from "@phosphor-icons/react";

const TABS = [
  { key: "all",         label: "All",            icon: Sparkle },
  { key: "promo_video", label: "Promo Videos",   icon: VideoCamera },
  { key: "flyer",       label: "Flyers",         icon: FilePdf },
  { key: "testimonial", label: "Testimonials",   icon: ChatCircleText },
  { key: "how_to",      label: "How-To",         icon: GraduationCap },
  { key: "press",       label: "Press",          icon: Newspaper },
];

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
};

const kindIcon = (kind) => {
  if (kind === "video_file" || kind === "video_embed") return VideoCamera;
  if (kind === "pdf") return FilePdf;
  if (kind === "image") return ImageIcon;
  return Megaphone;
};

export default function MediaLibrary() {
  const [active, setActive] = useState("all");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = active === "all" ? "" : `?category=${active}`;
    api.get(`/media${q}`)
      .then((r) => {
        setItems(r.data?.items || []);
        setCounts(r.data?.counts || {});
      })
      .catch(() => { setItems([]); })
      .finally(() => setLoading(false));
  }, [active]);

  const tabs = useMemo(
    () => TABS.map((t) => ({ ...t, n: counts?.[t.key] || 0 })),
    [counts],
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] font-manrope text-ink">
      <Helmet>
        <title>Media & Press — Armstrong K9 Registry</title>
        <meta
          name="description"
          content="Watch our promo videos, download flyers, read testimonials, learn how to use the registry, and browse press coverage of Armstrong K9 Registry."
        />
      </Helmet>

      {/* Top brand strip */}
      <header className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="media-brand-link">
            <img src="/logo-256.png" alt="" className="h-8 w-8 rounded-full object-contain" />
            <span className="font-spectral text-base tracking-tight">Armstrong K9 Registry</span>
          </Link>
          <nav className="hidden sm:flex items-center gap-5 text-sm text-[#3F3F3F]">
            <Link to="/about" className="hover:text-ink" data-testid="media-nav-about">About</Link>
            <Link to="/login" className="hover:text-ink" data-testid="media-nav-login">Sign in</Link>
            <Link
              to="/register"
              className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white px-4 py-1.5 text-xs font-semibold tracking-wide transition-colors"
              data-testid="media-nav-register"
            >
              Join the registry
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-bold">Media & Press</p>
        <h1 className="font-spectral text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3 max-w-3xl">
          Watch, read, and download — everything we publish, all in one place.
        </h1>
        <p className="text-base text-[#3F3F3F] mt-5 max-w-3xl leading-relaxed">
          Promo videos, flyers you can print, breeder and buyer testimonials,
          step-by-step how-to guides, and press coverage. Every item has a
          shareable link — paste it directly into an email or a text instead
          of sending people to social media.
        </p>
      </section>

      {/* Tabs */}
      <section className="max-w-6xl mx-auto px-6 md:px-10">
        <div
          className="flex flex-wrap gap-2 border-b border-[#E5E5E5] pb-3"
          role="tablist"
          data-testid="media-library-tabs"
        >
          {tabs.map((t) => {
            const I = t.icon;
            const isActive = active === t.key;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(t.key)}
                data-testid={`media-tab-${t.key}`}
                className={
                  "inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm transition-colors " +
                  (isActive
                    ? "bg-[#0E0E0E] text-white"
                    : "bg-white text-[#3F3F3F] border border-[#E5E5E5] hover:bg-[#F4F4F4]")
                }
              >
                <I size={16} weight={isActive ? "fill" : "regular"} />
                <span className="font-medium">{t.label}</span>
                {t.n > 0 && (
                  <span
                    className={
                      "text-xs rounded-full px-2 py-0.5 " +
                      (isActive ? "bg-white/15" : "bg-[#F4F4F4] text-[#666]")
                    }
                  >
                    {t.n}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-2xl bg-white border border-[#E5E5E5] h-64 animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div
            className="bg-white border border-[#E5E5E5] rounded-2xl p-10 text-center"
            data-testid="media-library-empty"
          >
            <Megaphone size={32} className="mx-auto text-[#9b9b9b]" />
            <p className="mt-3 font-spectral text-xl">Nothing here yet — check back soon.</p>
            <p className="mt-1 text-sm text-[#666]">
              We're recording new videos and printing new flyers every week.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="media-library-grid">
            {items.map((it) => <MediaCard key={it.id} item={it} />)}
          </div>
        )}
      </section>

      {/* Mini footer */}
      <footer className="bg-white border-t border-[#E5E5E5] py-8 mt-8">
        <div className="max-w-6xl mx-auto px-6 md:px-10 text-xs text-[#9b9b9b] flex flex-wrap items-center justify-between gap-3">
          <span>© Armstrong K9 Registry</span>
          <div className="flex items-center gap-4">
            <Link to="/about" className="hover:text-ink">About</Link>
            <Link to="/legal/terms" className="hover:text-ink">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-ink">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function MediaCard({ item }) {
  const Icon = kindIcon(item.kind);
  const cover = resolveUrl(item.cover_url);
  const isVideo = item.kind === "video_file" || item.kind === "video_embed";
  return (
    <Link
      to={`/media/${item.slug}`}
      data-testid={`media-card-${item.slug}`}
      className="group block bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden hover:shadow-lg transition-shadow"
    >
      <div className="relative aspect-video bg-[#F4F4F4] overflow-hidden">
        {cover ? (
          <img
            src={cover}
            alt={item.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#0E0E0E] to-[#1f1f1f]">
            <Icon size={48} className="text-gold" />
          </div>
        )}
        {isVideo && (
          <div className="absolute inset-0 flex items-center justify-center">
            <PlayCircle size={64} weight="fill" className="text-white/90 drop-shadow-lg group-hover:scale-110 transition-transform" />
          </div>
        )}
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-black/70 text-white text-[10px] uppercase tracking-wider px-2 py-1 rounded-full">
          <Icon size={12} />
          {labelFor(item.kind)}
        </span>
      </div>
      <div className="p-5">
        <p className="text-[10px] uppercase tracking-[0.18em] text-gold font-bold">
          {labelForCategory(item.category)}
        </p>
        <h3 className="font-spectral text-lg mt-1 leading-tight group-hover:text-[#C1272D] transition-colors">
          {item.title}
        </h3>
        {item.subtitle && (
          <p className="text-sm text-[#666] mt-1.5 line-clamp-2">{item.subtitle}</p>
        )}
      </div>
    </Link>
  );
}

function labelFor(kind) {
  return { video_file: "Video", video_embed: "Video", pdf: "PDF", image: "Image" }[kind] || "Media";
}
function labelForCategory(cat) {
  return {
    promo_video: "Promo",
    flyer: "Flyer",
    testimonial: "Testimonial",
    how_to: "How-To",
    press: "Press",
    other: "Media",
  }[cat] || "Media";
}
